/**
 * Carrier MCP — Pricing & Projects Tool Registration
 *
 * Registers 10 new MCP tools:
 *   - 5 pricing/billing management tools (credit_balance, configure_billing, etc.)
 *   - 5 projects/service management tools (service_catalog, credential_status, etc.)
 *
 * These tools follow the same wrapHandler pattern as OCS tools but skip
 * OCS token resolution (they operate on billing/config data only).
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import type { ToolContext } from "./tools.js";
import { checkCallQuota, recordUsage, UPGRADE_URL } from "./billing.js";
import { PRICING_TOOLS } from "./pricing-tools.js";
import { PROJECTS_TOOLS } from "./projects-tools.js";
import { checkCredits, deductCredits } from "./credits.js";
import type { Tier } from "./billing.js";

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/**
 * Register all pricing and projects tools on the MCP server.
 * Called from agent.ts init() alongside other tool registrations.
 */
export function registerAllPricingTools(
  server: McpServer,
  ctx: ToolContext,
): void {
  const allTools = [...PRICING_TOOLS, ...PROJECTS_TOOLS];

  for (const tool of allTools) {
    server.registerTool(
      tool.name,
      {
        title: toolNameToTitle(tool.name),
        description: tool.description,
        inputSchema: buildZodSchema(tool.inputSchema),
        annotations: {
          readOnlyHint: tool.scope === "read",
          ...(tool.scope !== "read" ? { destructiveHint: false } : {}),
        },
      },
      buildPricingHandler(tool, ctx),
    );
  }
}

// ---------------------------------------------------------------------------
// Handler Builder
// ---------------------------------------------------------------------------

type ToolResult = {
  content: Array<{ type: "text"; text: string }>;
  isError?: boolean;
};

function buildPricingHandler(
  tool: (typeof PRICING_TOOLS)[number] | (typeof PROJECTS_TOOLS)[number],
  ctx: ToolContext,
) {
  return async (args: Record<string, unknown>): Promise<ToolResult> => {
    const start = Date.now();

    // Scope enforcement
    if (!ctx.props.scope.includes(tool.scope as "read" | "write" | "admin")) {
      ctx.audit({
        tool_name: tool.name,
        ocs_method: "billing",
        status: "scope_denied",
        dry_run: false,
        duration_ms: 0,
      });
      return {
        isError: true,
        content: [
          {
            type: "text",
            text: `Scope denied: tool '${tool.name}' requires '${tool.scope}' scope. Your token has: [${ctx.props.scope.join(", ")}].`,
          },
        ],
      };
    }

    // Credit check (v2.0 system — runs in parallel with legacy quota check)
    const tier = ctx.props.tier as Tier;
    const creditResult = await checkCredits(ctx.env, ctx.props.sub, tier, tool.scope as "read" | "write" | "admin");

    if (!creditResult.allowed) {
      // Fall back to legacy quota check
      const quota = await checkCallQuota(ctx.env, ctx.props.sub, tier);
      if (!quota.allowed) {
        ctx.audit({
          tool_name: tool.name,
          ocs_method: "billing",
          status: "quota_exceeded",
          dry_run: false,
          duration_ms: 0,
        });
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: `Credit limit reached. Remaining: ${creditResult.credits_remaining} credits. Resets ${creditResult.reset_at}. Upgrade at ${UPGRADE_URL}`,
            },
          ],
        };
      }
    }

    // Execute handler
    try {
      const result = await tool.handler(ctx.env, ctx.props, args);

      // Record usage (both systems)
      recordUsage(ctx.env, ctx.props.sub, tier);
      deductCredits(ctx.env, ctx.props.sub, tier, tool.scope as "read" | "write" | "admin");

      ctx.audit({
        tool_name: tool.name,
        ocs_method: "billing",
        status: "ok",
        dry_run: false,
        duration_ms: Date.now() - start,
      });

      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(result, null, 2),
          },
        ],
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      ctx.audit({
        tool_name: tool.name,
        ocs_method: "billing",
        status: "error",
        dry_run: false,
        duration_ms: Date.now() - start,
      });
      return {
        isError: true,
        content: [{ type: "text", text: `Error: ${message}` }],
      };
    }
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toolNameToTitle(name: string): string {
  return name
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/**
 * Build a zod schema from the JSON Schema-like inputSchema definition.
 * Supports: string, number, boolean, array of numbers.
 */
function buildZodSchema(
  schema: Record<string, unknown>,
): Record<string, z.ZodTypeAny> {
  const properties = (schema.properties ?? {}) as Record<
    string,
    { type?: string; description?: string; enum?: string[]; items?: { type?: string } }
  >;
  const result: Record<string, z.ZodTypeAny> = {};

  for (const [key, prop] of Object.entries(properties)) {
    let field: z.ZodTypeAny;

    if (prop.enum) {
      field = z.enum(prop.enum as [string, ...string[]]);
    } else if (prop.type === "number") {
      field = z.number();
    } else if (prop.type === "boolean") {
      field = z.boolean();
    } else if (prop.type === "array") {
      if (prop.items?.type === "number") {
        field = z.array(z.number());
      } else {
        field = z.array(z.string());
      }
    } else {
      field = z.string();
    }

    // All pricing tool params are optional (no required fields in most cases)
    const required = (schema.required as string[] | undefined) ?? [];
    if (!required.includes(key)) {
      field = field.optional();
    }

    if (prop.description) {
      field = field.describe(prop.description);
    }

    result[key] = field;
  }

  return result;
}
